import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Trash2, FileText, Copy, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useSiteRoles } from '@/hooks/useSiteRoles';
import { usePermissionGuard } from '@/hooks/usePermissionGuard';
import { SiteSelector } from '@/components/SiteSelector';
import AppHeader from '@/components/AppHeader';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface Instruction {
  id: string;
  title: string;
  description: string;
  active: boolean;
  status: 'draft' | 'approved';
  created_at: string;
  revision: number;
  language: string;
  item_codes: string[] | null;
}

const InstructionList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { currentLineId, setCurrentLineId, currentTenantId } = useAuth();
  const { hasPermission, isReady: rolesReady } = useSiteRoles(currentLineId || undefined);
  const [instructions, setInstructions] = useState<Instruction[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // Check if user can edit instructions (site manager or admin)
  const canView = hasPermission('core.instructions.view');
  const canEdit = hasPermission('core.instructions.create') || hasPermission('core.instructions.edit');

  usePermissionGuard({
    enabled: rolesReady && !!currentLineId,
    allowed: canView,
    message: t('dashboard.permissionAccessDenied'),
  });

  useEffect(() => {
    loadInstructions();
  }, [currentTenantId, currentLineId]);

  const loadInstructions = async () => {
    try {
      console.log('🔍 [InstructionList] Loading work instructions...');
      if (!currentTenantId) {
        setInstructions([]);
        return;
      }

      const { data, error } = await ((supabase as any)
        .schema('mod_styling'))
        .from('work_instructions')
        .select('*')
        .eq('tenant_id', currentTenantId)
        .order('created_at', { ascending: false });

      console.log('🔍 [InstructionList] Query result:', {
        data,
        error,
        dataLength: data?.length,
        errorCode: error?.code,
        errorMessage: error?.message,
        errorDetails: error?.details
      });

      if (error) {
        console.error('❌ [InstructionList] Error loading instructions:', error);
        throw error;
      }

      const mapped = (data || []).map(item => ({
        ...item,
        status: (item.status as 'draft' | 'approved') || 'draft'
      }));

      console.log('✅ [InstructionList] Loaded instructions:', mapped.length);
      setInstructions(mapped);
    } catch (error: any) {
      console.error('💥 [InstructionList] Exception:', error);
      toast.error(error.message || t('errors.loadInstructions'));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;

    try {
      const { error } = await ((supabase as any)
        .schema('mod_styling'))
        .from('work_instructions')
        .delete()
        .eq('id', deleteId)
        .eq('tenant_id', currentTenantId);

      if (error) throw error;

      toast.success(t('instructions.deleted'));
      setInstructions(instructions.filter(i => i.id !== deleteId));
      setDeleteId(null);
    } catch (error: any) {
      toast.error(error.message || t('errors.deleteInstruction'));
    }
  };

  const handleDuplicate = async (instruction: Instruction) => {
    try {
      // Find the highest revision for this instruction title
      const { data: existingRevisions, error: revisionError } = await ((supabase as any)
        .schema('mod_styling'))
        .from('work_instructions')
        .select('revision')
        .eq('title', instruction.title)
        .eq('tenant_id', currentTenantId)
        .order('revision', { ascending: false })
        .limit(1);

      if (revisionError) throw revisionError;

      const newRevision = existingRevisions && existingRevisions.length > 0
        ? existingRevisions[0].revision + 1
        : instruction.revision + 1;

      // Fetch full instruction data including item_codes
      const { data: fullInstruction, error: fetchError } = await ((supabase as any)
        .schema('mod_styling'))
        .from('work_instructions')
        .select('*')
        .eq('id', instruction.id)
        .eq('tenant_id', currentTenantId)
        .single();

      if (fetchError) throw fetchError;

      // Create the new instruction with all fields including item_codes
      const { data: newInstruction, error: instructionError } = await ((supabase as any)
        .schema('mod_styling'))
        .from('work_instructions')
        .insert({
          tenant_id: currentTenantId,
          line_id: currentLineId,
          title: instruction.title,
          description: instruction.description,
          language: instruction.language || 'en',
          status: 'draft',
          active: false,
          revision: newRevision,
          item_codes: fullInstruction.item_codes || [],
        })
        .select()
        .single();

      if (instructionError) throw instructionError;

      // Copy all steps
      const { data: steps, error: stepsError } = await ((supabase as any)
        .schema('mod_styling'))
        .from('instruction_steps')
        .select('*')
        .eq('instruction_id', instruction.id)
        .eq('tenant_id', currentTenantId);

      if (stepsError) throw stepsError;

      if (steps && steps.length > 0) {
        const newSteps = steps.map(step => ({
          tenant_id: currentTenantId,
          instruction_id: newInstruction.id,
          step_number: step.step_number,
          title: step.title,
          description: step.description,
          image_url: step.image_url,
          is_quality_check: step.is_quality_check,
          parent_step_id: step.parent_step_id,
          language: step.language,
          part_number: step.part_number,
          tools_equipment: step.tools_equipment,
          ppe: step.ppe,
          tools_equipment_image_url: step.tools_equipment_image_url,
        }));

        const { error: insertStepsError } = await ((supabase as any)
          .schema('mod_styling'))
          .from('instruction_steps')
          .insert(newSteps);

        if (insertStepsError) throw insertStepsError;
      }

      toast.success(`${t('instructions.revisionCreated')} ${newRevision}`);
      loadInstructions();
    } catch (error: any) {
      toast.error(error.message || t('instructions.duplicateFailed'));
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-secondary via-background to-muted">
      <AppHeader
        backTo="/"
        backLabel={t('manager.backToDashboard')}
        rightContent={(
          <SiteSelector
            selectedSiteId={currentLineId || undefined}
            onSiteChange={setCurrentLineId}
            showWhenSingle
          />
        )}
        title={t('instructions.workInstructions')}
        subtitle={t('instructions.manageDesc')}
        action={
          canEdit && (
            <Button
              onClick={() => navigate('/manager/instructions/new')}
              size="lg"
            >
              <Plus className="mr-2 h-4 w-4" />
              {t('instructions.createInstruction')}
            </Button>
          )
        }
      />

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {loading ? (
            <div className="min-h-[40vh] flex items-center justify-center">
              <p>{t('common.loading')}</p>
            </div>
          ) : instructions.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center">
                <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-xl font-semibold mb-2">{t('instructions.noInstructions')}</h3>
                <p className="text-muted-foreground mb-4">
                  {t('instructions.noInstructionsDesc')}
                </p>
                <Button
                  onClick={() => navigate('/manager/instructions/new')}
                  disabled={!canEdit}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {t('instructions.createFirst')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {instructions.map((instruction) => (
                <Card key={instruction.id}>
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <CardTitle>{instruction.title}</CardTitle>
                          <Badge variant="outline" className="font-mono">
                            v{instruction.revision}
                          </Badge>
                          <Badge variant={instruction.status === 'approved' ? 'default' : 'secondary'}>
                            {instruction.status === 'approved' ? t('instructions.approved') : t('instructions.draft')}
                          </Badge>
                          <Badge variant={instruction.active ? 'outline' : 'secondary'}>
                            {instruction.active ? t('instructions.active') : t('instructions.inactive')}
                          </Badge>
                        </div>
                        {instruction.description && (
                          <CardDescription>{instruction.description}</CardDescription>
                        )}
                        {instruction.item_codes && instruction.item_codes.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-2">
                            {instruction.item_codes.map((code) => (
                              <Badge key={code} variant="outline" className="text-xs">
                                {code}
                              </Badge>
                            ))}
                          </div>
                        )}
                        <p className="text-xs text-muted-foreground mt-2">
                          {t('instructions.created')} {new Date(instruction.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/manager/instructions/preview/${instruction.id}`)}
                          title={t('instructions.preview')}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {canEdit && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDuplicate(instruction)}
                              title={t('instructions.createRevision')}
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/manager/instructions/edit/${instruction.id}`)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setDeleteId(instruction.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardHeader>
                </Card>
              ))}
            </div>
          )}
        </div>
      </main>

      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('instructions.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('instructions.deleteDesc')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('instructions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              {t('instructions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InstructionList;
